//src/pages/ExchangeEngine/index.js
import React, { useEffect, useState, useRef } from "react";
import styled, { keyframes, css } from "styled-components";
import AOS from "aos";
import "aos/dist/aos.css";
import { FiActivity, FiShield, FiMonitor, FiDatabase, FiCpu, FiCheck, FiArrowRight, FiServer } from "react-icons/fi";

const ExchangeEnginePage = () => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const headerRef = useRef(null);
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [animationTriggered, setAnimationTriggered] = useState(false);

  useEffect(() => {
    // AOS 애니메이션 라이브러리 초기화 - 더 안정적인 설정
    AOS.init({
      duration: 800,
      once: true, // 한 번만 애니메이션 실행되도록 변경
      offset: 100,
      easing: 'ease-out-cubic'
    });
    
    // 스크롤 이벤트 리스너
    const handleScroll = () => {
      const position = window.pageYOffset;
      setScrollPosition(position);

      // 통계 섹션 확인
      if (statsRef.current) {
        const statsTop = statsRef.current.getBoundingClientRect().top;
        if (statsTop < window.innerHeight * 0.75 && !statsVisible) {
          setStatsVisible(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // 컴포넌트 언마운트 시 정리
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [statsVisible]);

  // 스크롤을 기반으로 헤더 스타일 변경
  const headerOpacity = Math.max(0, Math.min(1, 1 - scrollPosition / 500));
  
  // 메인 섹션으로 스크롤
  const scrollToMain = () => {
    window.scrollTo({
      top: window.innerHeight,
      behavior: 'smooth'
    });
  };

  // 숫자 증가 애니메이션
  // const animateValue = (start, end, duration) => {
  //   let startTimestamp = null;
  //   const step = (timestamp) => {
  //     if (!startTimestamp) startTimestamp = timestamp;
  //     const progress = Math.min((timestamp - startTimestamp) / duration, 1);
  //     const value = Math.floor(progress * (end - start) + start);
      
  //     return value;
  //   };
  //   return step;
  // };

  return (
    <PageContainer>
      {/* 역동적인 헤더 섹션 */}
      <HeroSection ref={headerRef} style={{ opacity: headerOpacity }}>
        <HeroOverlay>
          <HeroContent>
            <HeroSuptitle data-aos="fade-down">G-VISION TECHNOLOGY</HeroSuptitle>
            <HeroTitle data-aos="fade-up" data-aos-delay="200">
              거래소 엔진 <TitleHighlight>솔루션</TitleHighlight>
            </HeroTitle>
            <HeroDescription data-aos="fade-up" data-aos-delay="400">
              안정성과 확장성을 갖춘 강력한 금융거래 엔진으로
              <br />24시간 중단 없는 서비스를 제공합니다.
            </HeroDescription>
            <HeroButtonsContainer data-aos="fade-up" data-aos-delay="600">
              <PrimaryButton>솔루션 소개서</PrimaryButton>
              <SecondaryButton>데모 신청</SecondaryButton>
            </HeroButtonsContainer>
          </HeroContent>
          <ScrollIndicator onClick={scrollToMain}>
            <ScrollIcon />
            <ScrollText>자세히 알아보기</ScrollText>
          </ScrollIndicator>
        </HeroOverlay>
        <HeroBackgroundContainer>
          <HeroBackground />
          <HeroVideoOverlay />
        </HeroBackgroundContainer>
      </HeroSection>

      {/* 핵심 통계 섹션 */}
      <StatsSection ref={statsRef}>
        <StatsContainer>
          <StatCard data-aos="zoom-in" data-aos-delay="0">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "100,000+" : "0"}
            </StatNumber>
            <StatName>초당 처리량</StatName>
            <StatDescription>초당 10만 건 이상의 주문을 처리하는 강력한 성능</StatDescription>
          </StatCard>
          
          <StatCard data-aos="zoom-in" data-aos-delay="100">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "99.99%" : "0%"}
            </StatNumber>
            <StatName>시스템 가용성</StatName>
            <StatDescription>연중무휴 서비스 제공을 위한 고가용성 인프라</StatDescription>
          </StatCard>
          
          <StatCard data-aos="zoom-in" data-aos-delay="200">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "<5ms" : "0ms"}
            </StatNumber>
            <StatName>지연 시간</StatName>
            <StatDescription>초저지연 시스템으로 실시간 거래 처리 보장</StatDescription>
          </StatCard>
          
          <StatCard data-aos="zoom-in" data-aos-delay="300">
            <StatNumber visible={statsVisible}>
              {statsVisible ? "50+" : "0"}
            </StatNumber>
            <StatName>고객사</StatName>
            <StatDescription>국내외 다양한 금융기관과 거래소가 선택한 엔진</StatDescription>
          </StatCard>
        </StatsContainer>
      </StatsSection>

      {/* 소개 섹션 */}
      <IntroSection>
        <SectionContainer>
          <SectionHeading>
            <SectionTitleTag data-aos="fade-right">ABOUT</SectionTitleTag>
            <SectionTitle data-aos="fade-right" data-aos-delay="100">
              최고의 <ColoredText>거래소 엔진</ColoredText> 솔루션
            </SectionTitle>
            <SectionSubtitle data-aos="fade-right" data-aos-delay="200">
              안정성, 보안성, 확장성을 모두 갖춘 엔터프라이즈급 거래 플랫폼
            </SectionSubtitle>
          </SectionHeading>

          <IntroContainer>
            <IntroImageColumn data-aos="fade-right">
              <IntroImageWrapper>
                <IntroImage src={require("../../aimg/logo_bitcoin.png")} alt="거래소 엔진" />
                <IntroImageDecoration />
              </IntroImageWrapper>
            </IntroImageColumn>
            
            <IntroContentColumn data-aos="fade-left" data-aos-delay="200">
              <IntroParagraph>
                그래비젼의 거래소 엔진은 금융 시장의 높은 요구사항을 충족하기 위해 설계된 고성능 거래 시스템입니다. 초당 10만건 이상의 주문을 처리할 수 있는 처리 능력과 함께, 5ms 이하의 초저지연 응답 시간을 제공합니다.
              </IntroParagraph>

              <IntroFeatureList>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FiActivity /></IntroFeatureIcon>
                  <IntroFeatureText>고성능 매칭 엔진으로 대량 거래도 실시간 처리</IntroFeatureText>
                </IntroFeatureItem>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FiShield /></IntroFeatureIcon>
                  <IntroFeatureText>다중 보안 프로토콜로 안전한 거래 환경 제공</IntroFeatureText>
                </IntroFeatureItem>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FiServer /></IntroFeatureIcon>
                  <IntroFeatureText>클라우드 및 온프레미스 환경 모두 지원</IntroFeatureText>
                </IntroFeatureItem>
                <IntroFeatureItem>
                  <IntroFeatureIcon><FiMonitor /></IntroFeatureIcon>
                  <IntroFeatureText>실시간 모니터링 및 이상 감지 시스템 내장</IntroFeatureText>
                </IntroFeatureItem>
              </IntroFeatureList>
              
              <LearnMoreButton>
                기술 문서 보기 <FiArrowRight />
              </LearnMoreButton>
            </IntroContentColumn>
          </IntroContainer>
        </SectionContainer>
      </IntroSection>

      {/* 핵심 기능 섹션 - 안정화된 버전 */}
      <FeaturesSection>
        <SectionContainer>
          <SectionHeading centered>
            <SectionTitleTag data-aos="fade-up">FEATURES</SectionTitleTag>
            <SectionTitle data-aos="fade-up">
              핵심 <ColoredText>기능</ColoredText>
            </SectionTitle>
            <SectionSubtitle data-aos="fade-up">
              그래비젼의 거래소 엔진이 제공하는 차별화된 기능
            </SectionSubtitle>
          </SectionHeading>

          <FeatureCardsSection>
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(0)}>
              <FeatureCardInner active={activeFeature === 0}>
                <FeatureCardIcon active={activeFeature === 0}>
                  <FiCpu />
                </FeatureCardIcon>
                <FeatureCardTitle>고성능 처리 엔진</FeatureCardTitle>
                <FeatureCardDescription>
                  초당 10만건 이상 주문 처리
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
            
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(1)}>
              <FeatureCardInner active={activeFeature === 1}>
                <FeatureCardIcon active={activeFeature === 1}>
                  <FiShield />
                </FeatureCardIcon>
                <FeatureCardTitle>고급 보안 시스템</FeatureCardTitle>
                <FeatureCardDescription>
                  다중 암호화 및 보안 프로토콜
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
            
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(2)}>
              <FeatureCardInner active={activeFeature === 2}>
                <FeatureCardIcon active={activeFeature === 2}>
                  <FiMonitor />
                </FeatureCardIcon>
                <FeatureCardTitle>실시간 모니터링</FeatureCardTitle>
                <FeatureCardDescription>
                  24시간 모니터링 및 알림 시스템
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
            
            <FeatureCard data-aos="zoom-in" onClick={() => setActiveFeature(3)}>
              <FeatureCardInner active={activeFeature === 3}>
                <FeatureCardIcon active={activeFeature === 3}>
                  <FiDatabase />
                </FeatureCardIcon>
                <FeatureCardTitle>이중화 구성</FeatureCardTitle>
                <FeatureCardDescription>
                  장애 대비 고가용성 아키텍처
                </FeatureCardDescription>
              </FeatureCardInner>
            </FeatureCard>
          </FeatureCardsSection>
          
          <FeatureDetailSection>
            {activeFeature === 0 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/exchage_feature_performance.png")} alt="고성능 처리 엔진" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>고성능 처리 엔진</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      그래비젼의 거래소 엔진은 초당 10만건 이상의 주문을 처리할 수 있는 강력한 성능을 자랑합니다. 
                      특허받은 매칭 알고리즘과 최적화된 데이터 구조를 통해 높은 처리량을 유지하면서도 낮은 지연 시간을 보장합니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> 초당 10만건 이상 주문 처리</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 5ms 이하의 초저지연 응답 시간</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 대용량 트래픽에도 안정적인 성능</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 수평 확장 지원으로 용량 확장 용이</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
            
            {activeFeature === 1 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/feature_security_high_protection.png")} alt="고급 보안 시스템" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>고급 보안 시스템</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      금융 시스템에 필수적인 보안을 위해 다중 암호화 및 보안 프로토콜을 적용합니다.
                      지속적인 보안 업데이트와 취약점 점검을 통해 외부 위협으로부터 시스템을 안전하게 보호합니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> 다중 암호화 및 보안 프로토콜</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> ISMS, ISO 27001 인증 준수</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 정기적인 보안 감사 및 취약점 점검</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 이상 거래 탐지 및 차단 시스템</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
            
            {activeFeature === 2 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/feature_monitoring.png")} alt="실시간 모니터링" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>실시간 모니터링</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      24시간 실시간 모니터링 시스템으로 서비스 이상을 즉시 감지하고 신속하게 대응합니다.
                      직관적인 대시보드와 알림 시스템을 통해 시스템 상태를 항상 파악할 수 있습니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> 실시간 모니터링 대시보드</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 이상 감지 및 자동 알림 시스템</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 성능 및 부하 모니터링</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 사용자 정의 알림 설정</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
            
            {activeFeature === 3 && (
              <FeatureDetailPanel>
                <FeatureDetailGrid>
                  <FeatureDetailImage>
                    <img src={require("../../aimg/feature_redundancy.png")} alt="이중화 구성" />
                  </FeatureDetailImage>
                  <FeatureDetailContent>
                    <FeatureDetailTitle>이중화 구성</FeatureDetailTitle>
                    <FeatureDetailDescription>
                      장애 대비 이중화 구성으로 서비스 중단 없이 안정적인 서비스를 제공합니다.
                      Active-Active 구성으로 시스템 장애 시에도 서비스 연속성을 보장합니다.
                    </FeatureDetailDescription>
                    <FeatureDetailList>
                      <FeatureDetailItem><FiCheck /> Active-Active 이중화 구성</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 지역 분산 데이터센터 지원</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 자동 장애 감지 및 복구</FeatureDetailItem>
                      <FeatureDetailItem><FiCheck /> 99.99% 서비스 가용성 보장</FeatureDetailItem>
                    </FeatureDetailList>
                  </FeatureDetailContent>
                </FeatureDetailGrid>
              </FeatureDetailPanel>
            )}
          </FeatureDetailSection>
        </SectionContainer>
      </FeaturesSection>

      {/* 기술 스펙 섹션 */}
      <TechSpecSection>
        <TechSpecBackground>
          <SectionContainer>
            <TechSpecContent data-aos="fade-up">
              <TechSpecLeft>
                <SectionHeading light>
                  <SectionTitleTag light>SPECIFICATIONS</SectionTitleTag>
                  <SectionTitle light>
                    기술 <ColoredText light>스펙</ColoredText>
                  </SectionTitle>
                  <SectionSubtitle light>
                    그래비젼 거래소 엔진의 세부 기술 사양
                  </SectionSubtitle>
                </SectionHeading>
                <TechSpecDescription>
                  그래비젼 거래소 엔진은 최신 기술과 아키텍처를 기반으로 구축되었으며, 
                  금융 시장의 엄격한 요구사항을 충족하도록 설계되었습니다.
                  높은 처리량, 낮은 지연 시간, 높은 가용성을 제공하여 중단 없는 거래 서비스를 보장합니다.
                </TechSpecDescription>
                <TechSpecDownloadButton>
                  기술 명세서 다운로드 <FiArrowRight />
                </TechSpecDownloadButton>
              </TechSpecLeft>
              
              <TechSpecRight>
                <TechSpecTable>
                  <TechSpecRow>
                    <TechSpecLabel>처리 용량</TechSpecLabel>
                    <TechSpecValue>초당 10만건 이상 주문 처리</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>지연 시간</TechSpecLabel>
                    <TechSpecValue>평균 5ms 이하</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>가용성</TechSpecLabel>
                    <TechSpecValue>99.99% 이상</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>확장성</TechSpecLabel>
                    <TechSpecValue>수평적 확장 지원</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>보안 표준</TechSpecLabel>
                    <TechSpecValue>ISO 27001, PCI DSS 준수</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>데이터 백업</TechSpecLabel>
                    <TechSpecValue>실시간 및 일일 백업</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>API 지원</TechSpecLabel>
                    <TechSpecValue>REST, WebSocket, FIX 프로토콜</TechSpecValue>
                  </TechSpecRow>
                  <TechSpecRow>
                    <TechSpecLabel>배포 옵션</TechSpecLabel>
                    <TechSpecValue>클라우드, 온프레미스, 하이브리드</TechSpecValue>
                  </TechSpecRow>
                </TechSpecTable>
              </TechSpecRight>
            </TechSpecContent>
          </SectionContainer>
        </TechSpecBackground>
      </TechSpecSection>

      {/* 구현 사례 섹션 */}
      <CaseStudySection>
        <SectionContainer>
          <SectionHeading centered>
            <SectionTitleTag data-aos="fade-up">CASE STUDIES</SectionTitleTag>
            <SectionTitle data-aos="fade-up" data-aos-delay="100">
              구현 <ColoredText>사례</ColoredText>
            </SectionTitle>
            <SectionSubtitle data-aos="fade-up" data-aos-delay="200">
              그래비젼 거래소 엔진을 도입한 고객 성공 사례
            </SectionSubtitle>
          </SectionHeading>
          
          <CaseStudyGrid>
            <CaseStudyCard 
              data-aos="fade-up"
              data-aos-delay="0"
              onMouseEnter={() => {
                if (!animationTriggered) {
                  setAnimationTriggered(true);
                }
              }}
            >
              <CaseStudyImageContainer>
                <CaseStudyImage src={require("../../aimg/case1.png")} alt="암호화폐 거래소" />
                <CaseStudyCategory>금융</CaseStudyCategory>
              </CaseStudyImageContainer>
              <CaseStudyContent>
                <CaseStudyTitle>국내 대형 암호화폐 거래소</CaseStudyTitle>
                <CaseStudyDescription>
                  일 평균 거래량 5천억원의 대형 거래소에 안정적인 거래 엔진을 제공하여 
                  시스템 안정성 확보 및 트래픽 급증 시에도 안정적인 서비스 제공
                </CaseStudyDescription>
                <CaseStudyResults>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>300%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>처리량 증가</CaseStudyResultLabel>
                  </CaseStudyResult>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>90%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>지연시간 감소</CaseStudyResultLabel>
                  </CaseStudyResult>
                </CaseStudyResults>
              </CaseStudyContent>
            </CaseStudyCard>
            
            <CaseStudyCard data-aos="fade-up" data-aos-delay="100">
              <CaseStudyImageContainer>
                <CaseStudyImage src={require("../../aimg/case2.png")} alt="글로벌 금융 서비스" />
                <CaseStudyCategory>글로벌</CaseStudyCategory>
              </CaseStudyImageContainer>
              <CaseStudyContent>
                <CaseStudyTitle>글로벌 금융 서비스</CaseStudyTitle>
                <CaseStudyDescription>
                  다국적 금융 서비스 기업에 확장 가능한 거래 플랫폼을 구축하여 
                  국제 금융 거래를 지원하고 다양한 규제 요구사항 충족
                </CaseStudyDescription>
                <CaseStudyResults>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>24/7</CaseStudyResultNumber>
                    <CaseStudyResultLabel>서비스 가용성</CaseStudyResultLabel>
                  </CaseStudyResult>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>15+</CaseStudyResultNumber>
                    <CaseStudyResultLabel>국가 지원</CaseStudyResultLabel>
                  </CaseStudyResult>
                </CaseStudyResults>
              </CaseStudyContent>
            </CaseStudyCard>
            
            <CaseStudyCard data-aos="fade-up" data-aos-delay="200">
              <CaseStudyImageContainer>
                <CaseStudyImage src={require("../../aimg/case3.png")} alt="핀테크 스타트업" />
                <CaseStudyCategory>핀테크</CaseStudyCategory>
              </CaseStudyImageContainer>
              <CaseStudyContent>
                <CaseStudyTitle>핀테크 스타트업</CaseStudyTitle>
                <CaseStudyDescription>
                  빠르게 성장하는 핀테크 스타트업에 맞춤형 거래 시스템을 제공하여 
                  초기 비용 부담 없이 확장 가능한 인프라로 비즈니스 성장 지원
                </CaseStudyDescription>
                <CaseStudyResults>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>60%</CaseStudyResultNumber>
                    <CaseStudyResultLabel>비용 절감</CaseStudyResultLabel>
                  </CaseStudyResult>
                  <CaseStudyResult>
                    <CaseStudyResultNumber>5x</CaseStudyResultNumber>
                    <CaseStudyResultLabel>사용자 증가</CaseStudyResultLabel>
                  </CaseStudyResult>
                </CaseStudyResults>
              </CaseStudyContent>
            </CaseStudyCard>
          </CaseStudyGrid>
          
          <ViewMoreButtonContainer data-aos="fade-up">
            <ViewMoreButton>
              모든 사례 보기 <FiArrowRight />
            </ViewMoreButton>
          </ViewMoreButtonContainer>
        </SectionContainer>
      </CaseStudySection>

      {/* CTAs 섹션 */}
      <CTASection>
        <SectionContainer>
          <CTAContainer data-aos="fade-up">
            <CTAContent>
              <CTATitle>
                빠르고 안정적인 <ColoredText>거래 플랫폼</ColoredText>이 필요하신가요?
              </CTATitle>
              <CTADescription>
                그래비젼의 거래소 엔진 솔루션으로 차별화된 금융 서비스를 제공하세요.
                <br />전문가와 상담을 통해 귀사에 최적화된 솔루션을 안내해 드립니다.
              </CTADescription>
              <CTAButtonsContainer>
                <CTAPrimaryButton>데모 신청하기</CTAPrimaryButton>
                <CTASecondaryButton>문의하기</CTASecondaryButton>
              </CTAButtonsContainer>
            </CTAContent>
            <CTAImageContainer>
              <CTAImage src={require("../../aimg/cta_image.png")} alt="데모 신청" />
            </CTAImageContainer>
          </CTAContainer>
        </SectionContainer>
      </CTASection>

      {/* 추가 제품 정보 섹션 */}
      <RelatedSection>
        <SectionContainer>
          <SectionHeading centered>
            <SectionTitleTag data-aos="fade-up">RELATED SOLUTIONS</SectionTitleTag>
            <SectionTitle data-aos="fade-up" data-aos-delay="100">
              함께 살펴보면 좋은 <ColoredText>솔루션</ColoredText>
            </SectionTitle>
          </SectionHeading>
          
          <RelatedProductGrid>
            <RelatedProductCard data-aos="fade-up" data-aos-delay="0">
              <RelatedProductImage src={require("../../aimg/logo_deepleaning1.png")} alt="딥러닝" />
              <RelatedProductTitle>딥러닝</RelatedProductTitle>
              <RelatedProductDescription>
                방대한 데이터를 분석하여 고도의 패턴 인식과 예측을 실현합니다.
              </RelatedProductDescription>
              <RelatedProductLink>
                자세히 보기 <FiArrowRight />
              </RelatedProductLink>
            </RelatedProductCard>
            
            <RelatedProductCard data-aos="fade-up" data-aos-delay="100">
              <RelatedProductImage src={require("../../aimg/logo_ai_modeling1.png")} alt="AI 모델링" />
              <RelatedProductTitle>AI 모델링</RelatedProductTitle>
              <RelatedProductDescription>
                최신 AI 알고리즘으로 맞춤형 데이터 분석 모델을 개발합니다.
              </RelatedProductDescription>
              <RelatedProductLink>
                자세히 보기 <FiArrowRight />
              </RelatedProductLink>
            </RelatedProductCard>
            
            <RelatedProductCard data-aos="fade-up" data-aos-delay="200">
              <RelatedProductImage src={require("../../aimg/logo_isms.png")} alt="ISMS 인증" />
              <RelatedProductTitle>ISMS 인증</RelatedProductTitle>
              <RelatedProductDescription>
                높은 수준의 정보보안 체계를 구축하고 인증받은 안전한 시스템을 제공합니다.
              </RelatedProductDescription>
              <RelatedProductLink>
                자세히 보기 <FiArrowRight />
              </RelatedProductLink>
            </RelatedProductCard>
          </RelatedProductGrid>
        </SectionContainer>
      </RelatedSection>
    </PageContainer>
  );
};

// 애니메이션 정의
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

// const slideInRight = keyframes`
//   from { transform: translateX(50px); opacity: 0; }
//   to { transform: translateX(0); opacity: 1; }
// `;

// const slideInLeft = keyframes`
//   from { transform: translateX(-50px); opacity: 0; }
//   to { transform: translateX(0); opacity: 1; }
// `;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const float = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

// const bounce = keyframes`
//   0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
//   40% { transform: translateY(-20px); }
//   60% { transform: translateY(-10px); }
// `;

const scrollDown = keyframes`
  0% { transform: translateY(0); opacity: 0; }
  50% { opacity: 1; }
  100% { transform: translateY(20px); opacity: 0; }
`;

// 스타일 컴포넌트
const PageContainer = styled.div`
  width: 100%;
  overflow-x: hidden;
  font-family: 'Noto Sans KR', sans-serif;
  padding-top: 80px; /* 네비게이션 바 높이 */
  
  @media (max-width: 768px) {
    padding-top: 60px;
  }
`;

// 헤더 섹션
const HeroSection = styled.section`
  width: 100%;
  height: 100vh;
  position: relative;
  min-height: 700px;
  
  @media (max-width: 768px) {
    min-height: 600px;
  }
`;

const HeroBackgroundContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: -1;
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: url(${require("../../aimg/carousel1bg.png")}) no-repeat;
  background-size: cover;
  background-position: center;
  animation: ${pulse} 15s infinite ease-in-out;
`;

const HeroVideoOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, rgba(64, 72, 226, 0.8) 0%, rgba(0, 0, 0, 0.8) 100%);
`;

const HeroOverlay = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  width: 100%;
  padding: 0 20px;
  text-align: center;
  color: #ffffff;
`;

const HeroSuptitle = styled.div`
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 3px;
  margin-bottom: 20px;
  opacity: 0.9;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.9);
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const HeroTitle = styled.h1`
  font-size: 72px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 30px;
  letter-spacing: -1px;
  
  @media (max-width: 768px) {
    font-size: 42px;
  }
`;

const TitleHighlight = styled.span`
  color: #4048E2;
  display: inline-block;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 0;
    width: 100%;
    height: 10px;
    background-color: rgba(64, 72, 226, 0.3);
    z-index: -1;
    
    @media (max-width: 768px) {
      height: 6px;
      bottom: 3px;
    }
  }
`;

const HeroDescription = styled.p`
  font-size: 24px;
  font-weight: 300;
  margin-bottom: 40px;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  opacity: 0.9;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 30px;
  }
`;

const HeroButtonsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }
`;

const PrimaryButton = styled.button`
  background-color: #4048E2;
  color: white;
  font-size: 18px;
  font-weight: 500;
  padding: 15px 35px;
  border-radius: 30px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 5px 15px rgba(64, 72, 226, 0.4);
  
  &:hover {
    background-color: #3038C2;
    transform: translateY(-3px);
    box-shadow: 0 8px 20px rgba(64, 72, 226, 0.6);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 30px;
    width: 100%;
    max-width: 250px;
  }
`;

const SecondaryButton = styled.button`
  background-color: transparent;
  color: white;
  font-size: 18px;
  font-weight: 500;
  padding: 15px 35px;
  border-radius: 30px;
  border: 2px solid white;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    padding: 12px 30px;
    width: 100%;
    max-width: 250px;
  }
`;

const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 40px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  cursor: pointer;
  transition: opacity 0.3s ease;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ScrollIcon = styled.div`
  width: 30px;
  height: 50px;
  border: 2px solid white;
  border-radius: 25px;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 10px;
    left: 50%;
    width: 6px;
    height: 6px;
    background-color: white;
    border-radius: 50%;
    transform: translateX(-50%);
    animation: ${scrollDown} 2s infinite;
  }
`;

const ScrollText = styled.div`
  color: white;
  font-size: 14px;
  margin-top: 10px;
  font-weight: 300;
`;

// 통계 섹션
const StatsSection = styled.section`
  padding: 80px 0;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const StatsContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  padding: 0 20px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatCard = styled.div`
  background: white;
  border-radius: 15px;
  padding: 40px 30px;
  text-align: center;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const StatNumber = styled.div`
  font-size: 48px;
  font-weight: 700;
  color: #4048E2;
  margin-bottom: 15px;
  transition: opacity 0.5s ease-out;
  
  ${props => props.visible && css`
    animation: ${fadeIn} 1s forwards;
  `}
  
  @media (max-width: 768px) {
    font-size: 36px;
  }
`;

const StatName = styled.div`
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 10px;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const StatDescription = styled.div`
  font-size: 16px;
  color: #666;
  line-height: 1.5;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

// 소개 섹션
const IntroSection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const SectionContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const SectionHeading = styled.div`
  margin-bottom: 60px;
  text-align: ${props => props.centered ? 'center' : 'left'};
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const SectionTitleTag = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${props => props.light ? 'rgba(255, 255, 255, 0.8)' : '#4048E2'};
  margin-bottom: 15px;
  letter-spacing: 2px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 48px;
  font-weight: 700;
  color: ${props => props.light ? 'white' : '#333'};
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 32px;
  }
`;

const ColoredText = styled.span`
  color: ${props => props.light ? 'white' : '#4048E2'};
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 5px;
    left: 0;
    width: 100%;
    height: 10px;
    background-color: ${props => props.light ? 'rgba(255, 255, 255, 0.2)' : 'rgba(64, 72, 226, 0.2)'};
    z-index: -1;
    
    @media (max-width: 768px) {
      height: 6px;
      bottom: 3px;
    }
  }
`;

const SectionSubtitle = styled.p`
  font-size: 20px;
  font-weight: 300;
  color: ${props => props.light ? 'rgba(255, 255, 255, 0.8)' : '#666'};
  max-width: 600px;
  margin: ${props => props.centered ? '0 auto' : '0'};
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const IntroContainer = styled.div`
  display: flex;
  gap: 50px;
  align-items: center;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const IntroImageColumn = styled.div`
  flex: 1;
  
  @media (max-width: 1024px) {
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
  }
`;

const IntroImageWrapper = styled.div`
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    width: 100%;
    height: 100%;
    border: 2px solid #4048E2;
    border-radius: 10px;
    z-index: -1;
  }
`;

const IntroImage = styled.img`
  width: 100%;
  border-radius: 10px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const IntroImageDecoration = styled.div`
  position: absolute;
  top: 20px;
  right: -20px;
  width: 100px;
  height: 100px;
  background: rgba(64, 72, 226, 0.1);
  border-radius: 50%;
  z-index: -1;
  animation: ${float} 6s infinite ease-in-out;
`;

const IntroContentColumn = styled.div`
  flex: 1;
`;

const IntroParagraph = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #444;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const IntroFeatureList = styled.ul`
  list-style: none;
  padding: 0;
  margin-bottom: 40px;
`;

const IntroFeatureItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 20px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const IntroFeatureIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: rgba(64, 72, 226, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 15px;
  color: #4048E2;
  font-size: 20px;
`;

const IntroFeatureText = styled.div`
  font-size: 16px;
  color: #444;
`;

const LearnMoreButton = styled.button`
  background: none;
  border: none;
  color: #4048E2;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: 0;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
`;

// 기능 섹션 (개선된 버전)
const FeaturesSection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

// 카드 섹션
const FeatureCardsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 25px;
  margin-bottom: 60px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 15px;
  }
`;

const FeatureCard = styled.div`
  cursor: pointer;
  transform: translateY(0);
  transition: transform 0.4s ease;
  
  &:hover {
    transform: translateY(-10px);
  }
`;

const FeatureCardInner = styled.div`
  background-color: ${props => props.active ? '#4048E2' : 'white'};
  color: ${props => props.active ? 'white' : '#333'};
  border-radius: 15px;
  padding: 35px 25px;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  box-shadow: ${props => props.active 
    ? '0 15px 35px rgba(64, 72, 226, 0.4)' 
    : '0 10px 25px rgba(0, 0, 0, 0.05)'};
  transition: all 0.4s ease;
  
  &:hover {
    box-shadow: ${props => props.active 
      ? '0 20px 40px rgba(64, 72, 226, 0.5)' 
      : '0 15px 35px rgba(0, 0, 0, 0.1)'};
  }
  
  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const FeatureCardIcon = styled.div`
  font-size: 40px;
  margin-bottom: 20px;
  color: ${props => props.active ? 'white' : '#4048E2'};
  transition: all 0.3s ease;
  
  ${FeatureCardInner}:hover & {
    animation: ${pulse} 1s infinite;
  }
  
  @media (max-width: 768px) {
    font-size: 32px;
    margin-bottom: 15px;
  }
`;

const FeatureCardTitle = styled.h3`
  font-size: 22px;
  font-weight: 700;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 18px;
    margin-bottom: 10px;
  }
`;

const FeatureCardDescription = styled.p`
  font-size: 16px;
  font-weight: 400;
  line-height: 1.6;
  opacity: 0.8;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const FeatureDetailSection = styled.div`
  position: relative;
  min-height: 500px;
  
  @media (max-width: 768px) {
    min-height: 400px;
  }
`;

const FeatureDetailPanel = styled.div`
  background-color: white;
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.5s ease;
  
  @media (max-width: 768px) {
    padding: 25px;
  }
`;

const FeatureDetailGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: center;
  
  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const FeatureDetailImage = styled.div`
  img {
    width: 100%;
    border-radius: 15px;
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.1);
  }
`;

const FeatureDetailContent = styled.div`
  
`;

const FeatureDetailTitle = styled.h3`
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 20px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 24px;
    margin-bottom: 15px;
  }
`;

const FeatureDetailDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: #555;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 20px;
  }
`;

const FeatureDetailList = styled.ul`
  list-style: none;
  padding: 0;
`;

const FeatureDetailItem = styled.li`
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 18px;
  color: #444;
  
  svg {
    color: #4048E2;
    margin-right: 15px;
    flex-shrink: 0;
    font-size: 20px;
  }
  
  @media (max-width: 768px) {
    font-size: 16px;
    margin-bottom: 12px;
    
    svg {
      font-size: 18px;
      margin-right: 10px;
    }
  }
`;

// 기술 스펙 섹션
const TechSpecSection = styled.section`
  padding: 100px 0;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const TechSpecBackground = styled.div`
  background: linear-gradient(135deg, #373B98 0%, #4048E2 100%);
  border-radius: 30px;
  padding: 80px 40px;
  
  @media (max-width: 768px) {
    padding: 40px 20px;
    border-radius: 20px;
  }
`;

const TechSpecContent = styled.div`
  display: flex;
  gap: 60px;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 40px;
  }
`;

const TechSpecLeft = styled.div`
  flex: 1;
`;

const TechSpecDescription = styled.p`
  font-size: 18px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const TechSpecDownloadButton = styled.button`
  background: white;
  color: #4048E2;
  border: none;
  border-radius: 30px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: rgba(255, 255, 255, 0.9);
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
    
    svg {
      transform: translateX(5px);
    }
  }
  
  @media (max-width: 768px) {
    padding: 12px 25px;
    font-size: 14px;
  }
`;

const TechSpecRight = styled.div`
  flex: 1;
`;

const TechSpecTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 15px;
  overflow: hidden;
`;

const TechSpecRow = styled.tr`
  &:not(:last-child) {
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  }
`;

const TechSpecLabel = styled.td`
  padding: 20px;
  font-size: 16px;
  font-weight: 600;
  color: white;
  width: 40%;
  
  @media (max-width: 768px) {
    padding: 15px;
    font-size: 14px;
  }
`;

const TechSpecValue = styled.td`
  padding: 20px;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.8);
  
  @media (max-width: 768px) {
    padding: 15px;
    font-size: 14px;
  }
`;

// 구현 사례 섹션
const CaseStudySection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const CaseStudyGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 60px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CaseStudyCard = styled.div`
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  }
`;

const CaseStudyImageContainer = styled.div`
  position: relative;
  height: 200px;
  overflow: hidden;
`;

const CaseStudyImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
  
  ${CaseStudyCard}:hover & {
    transform: scale(1.1);
  }
`;

const CaseStudyCategory = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  background: #4048E2;
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 8px 15px;
  border-radius: 20px;
`;

const CaseStudyContent = styled.div`
  padding: 30px;
`;

const CaseStudyTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const CaseStudyDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #555;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const CaseStudyResults = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

const CaseStudyResult = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CaseStudyResultNumber = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #4048E2;
  margin-bottom: 5px;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
`;

const CaseStudyResultLabel = styled.div`
  font-size: 13px;
  color: #666;
  text-align: center;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const ViewMoreButtonContainer = styled.div`
  display: flex;
  justify-content: center;
`;

const ViewMoreButton = styled.button`
  background: none;
  border: 2px solid #4048E2;
  color: #4048E2;
  font-size: 16px;
  font-weight: 600;
  padding: 12px 30px;
  border-radius: 30px;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    background: #4048E2;
    color: white;
    
    svg {
      transform: translateX(5px);
    }
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 10px 25px;
  }
`;

// CTA 섹션
const CTASection = styled.section`
  padding: 100px 0;
  background-color: #f9f9f9;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const CTAContainer = styled.div`
  display: flex;
  gap: 60px;
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
  
  @media (max-width: 1024px) {
    flex-direction: column;
    gap: 30px;
  }
`;

const CTAContent = styled.div`
  flex: 1;
  padding: 60px;
  
  @media (max-width: 768px) {
    padding: 30px;
  }
`;

const CTATitle = styled.h2`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 20px;
  line-height: 1.3;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 24px;
  }
`;

const CTADescription = styled.p`
  font-size: 18px;
  line-height: 1.7;
  color: #555;
  margin-bottom: 40px;
  
  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const CTAButtonsContainer = styled.div`
  display: flex;
  gap: 20px;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 15px;
  }
`;

const CTAPrimaryButton = styled.button`
  background: #4048E2;
  color: white;
  border: none;
  border-radius: 30px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 20px rgba(64, 72, 226, 0.2);
  
  &:hover {
    background: #3038C2;
    transform: translateY(-3px);
    box-shadow: 0 15px 25px rgba(64, 72, 226, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 25px;
  }
`;

const CTASecondaryButton = styled.button`
  background: none;
  border: 2px solid #4048E2;
  color: #4048E2;
  border-radius: 30px;
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(64, 72, 226, 0.1);
    transform: translateY(-3px);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 12px 25px;
  }
`;

const CTAImageContainer = styled.div`
  flex: 1;
  min-height: 400px;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 1024px) {
    min-height: 300px;
  }
`;

const CTAImage = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

// 관련 제품 섹션
const RelatedSection = styled.section`
  padding: 100px 0;
  background-color: white;
  
  @media (max-width: 768px) {
    padding: 80px 0;
  }
`;

const RelatedProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const RelatedProductCard = styled.div`
  background: #f9f9f9;
  border-radius: 15px;
  padding: 30px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.05);
  }
`;

const RelatedProductImage = styled.img`
  width: 160px;
  height: 160px;
  object-fit: cover;
  border-radius: 10px;
  margin-bottom: 20px;
`;

const RelatedProductTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 15px;
  color: #333;
  
  @media (max-width: 768px) {
    font-size: 18px;
  }
`;

const RelatedProductDescription = styled.p`
  font-size: 15px;
  line-height: 1.6;
  color: #555;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

const RelatedProductLink = styled.a`
  color: #4048E2;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  cursor: pointer;
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover svg {
    transform: translateX(5px);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
  }
`;

export default ExchangeEnginePage;